import React from 'react';
import {Controller} from 'react-hook-form';
import {
	IconButtonCompat as IconButton,
	LabelCompat as Label,
	StackCompat as Stack,
	TextFieldCompat as TextField,
} from 'components/FluentUICompat';
import {ErrorMessage} from './ErrorMessage';
import {HookFormProps} from './HookFormProps';

/**
 * Pragmatic, dependency-free syntactic check: exactly one "@", a dotted domain, and no
 * whitespace. Exported so it can be unit-tested in isolation.
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (value: string): boolean =>
	EMAIL_REGEX.test(value.trim());

export type ControlledEmailChipsProps = HookFormProps & {
	label: string;
	placeholder?: string;
	invalidEmailMessage?: string;
	duplicateEmailMessage?: string;
};

/**
 * A controlled, free-text multi-email input. The field value is a `string[]`; each committed
 * address renders as a removable chip. Addresses are committed on Enter, comma, or blur, and are
 * validated for syntactic well-formedness (invalid/duplicate entries are rejected with an inline
 * message rather than added).
 */
export const ControlledEmailChips: React.FC<ControlledEmailChipsProps> = props => {
	const [inputValue, setInputValue] = React.useState('');
	const [localError, setLocalError] = React.useState<string>();

	return (
		<Controller
			name={props.name}
			control={props.control}
			rules={props.rules}
			defaultValue={props.defaultValue ?? []}
			render={({field: {onChange, onBlur, value}, fieldState: {error}}) => {
				const emails: string[] = Array.isArray(value) ? value : [];

				const addEmail = (raw: string): void => {
					const candidate = raw.trim();
					if (candidate === '') {
						return;
					}
					if (!isValidEmail(candidate)) {
						setLocalError(
							props.invalidEmailMessage ??
								`"${candidate}" is not a valid e-mail address.`,
						);
						return;
					}
					if (emails.some(e => e.toLowerCase() === candidate.toLowerCase())) {
						setLocalError(
							props.duplicateEmailMessage ??
								`"${candidate}" has already been added.`,
						);
						setInputValue('');
						return;
					}
					onChange([...emails, candidate]);
					setInputValue('');
					setLocalError(undefined);
				};

				const removeEmail = (index: number): void => {
					onChange(emails.filter((_, i) => i !== index));
				};

				return (
					<Stack tokens={{childrenGap: 6}}>
						<Label required={Boolean(props.rules?.required)}>
							{props.label}
						</Label>

						{emails.length > 0 && (
							<div
								style={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: 6,
								}}
							>
								{emails.map((email, index) => (
									<div
										key={email}
										style={{
											display: 'flex',
											alignItems: 'center',
											backgroundColor: '#f3f2f1',
											borderRadius: 2,
											paddingLeft: 8,
										}}
									>
										<span>{email}</span>
										<IconButton
											iconProps={{iconName: 'Cancel'}}
											ariaLabel={`Remove ${email}`}
											title={`Remove ${email}`}
											onClick={() => removeEmail(index)}
										/>
									</div>
								))}
							</div>
						)}

						<TextField
							value={inputValue}
							placeholder={props.placeholder}
							onChange={(_, newValue) => {
								setInputValue(newValue ?? '');
								if (localError) {
									setLocalError(undefined);
								}
							}}
							onKeyDown={event => {
								if (event.key === 'Enter' || event.key === ',') {
									event.preventDefault();
									addEmail(inputValue);
								}
							}}
							onBlur={() => {
								if (inputValue.trim() !== '') {
									addEmail(inputValue);
								}
								onBlur();
							}}
						/>

						<ErrorMessage error={error} />
						{localError && (
							<div
								role='alert'
								style={{color: 'rgb(164, 38, 44)', fontSize: 12}}
							>
								{localError}
							</div>
						)}
					</Stack>
				);
			}}
		/>
	);
};
